import { Button } from '@/components/Button/Button';
import { Input } from '@/components/Input/Input';
import { useDebounce } from '@/hooks/useDebounce';
import { useCountriesCoordinates } from '@api/WhisApi/generated';
import { FieldTitle } from '@components/FieldTitle/FieldTitle';
import PinModal from '@components/PinModal/PinModal';
import { Ionicons } from '@expo/vector-icons';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { FieldError } from '@screens/ReportCreation/components/fields/FieldError';
import { useState } from 'react';
import { SizableText, XStack, YStack } from 'tamagui';
import { formatCoordinateInput } from './locationField.utils';

interface LocationFieldProps {
  /** Field label (displayed above the field) */
  label?: string;
  /** Current latitude value */
  latitude: string | null | undefined;
  /** Current longitude value */
  longitude: string | null | undefined;
  /** Callback when latitude changes */
  onLatitudeChange: (value: string) => void;
  /** Callback when longitude changes */
  onLongitudeChange: (value: string) => void;
  /** Callback when pin button is pressed */
  onPinPress?: () => void;
  /** Placeholder for latitude input */
  latitudePlaceholder?: string;
  /** Placeholder for longitude input */
  longitudePlaceholder?: string;
  /** Whether the field is required */
  required?: boolean;
}

/**
 * Local component to display required indicator (*) on empty inputs
 */
const RequiredIndicator = ({ show }: { show: boolean }): React.ReactNode => {
  if (!show) return null;

  return (
    <SizableText
      pointerEvents="none"
      size="$1"
      color="$brand"
      position="absolute"
      top="$3"
      right="$2"
    >
      *
    </SizableText>
  );
};

/**
 * LocationField - A field for entering geographic coordinates (latitude and longitude)
 *
 * Features:
 * - Two numeric input fields for latitude and longitude
 * - Pin button for GPS/map selection
 * - Optional label above the field
 * - Required indicator (*)
 * - Placeholder support for both inputs
 *
 * @example
 * <LocationField
 *   latitude={lat}
 *   longitude={lng}
 *   onLatitudeChange={setLat}
 *   onLongitudeChange={setLng}
 *   onPinPress={handlePinPress}
 *   latitudePlaceholder="Latitude"
 *   longitudePlaceholder="Longitude"
 *   required
 * />
 */
export const LocationField = ({
  label,
  latitude,
  longitude,
  onLatitudeChange,
  onLongitudeChange,
  onPinPress,
  latitudePlaceholder = 'Latitude',
  longitudePlaceholder = 'Longitude',
  required = false,
}: LocationFieldProps): React.ReactElement => {
  const { t } = useAppTranslation();
  const [pinVisible, setPinVisible] = useState(false);
  const debouncedLatitude = useDebounce(latitude, 400);
  const debouncedLongitude = useDebounce(longitude, 400);
  const coordinatesEnabled =
    debouncedLongitude &&
    !isNaN(+debouncedLongitude) &&
    debouncedLatitude &&
    !isNaN(+debouncedLatitude);
  const { data: country, error } = useCountriesCoordinates(
    {
      latitude: +debouncedLatitude!,
      longitude: +debouncedLongitude!,
    },
    {
      query: {
        enabled: !!coordinatesEnabled,
        retry: (failureCount, error1) => {
          // Retry only if status code isn't a know 400 request error or failure count hasn't exceeded 10 tries
          return error1.statusCode !== 400 && failureCount <= 10;
        },
      },
    },
  );

  return (
    <YStack space="$2">
      {/* Label */}
      <FieldTitle label={label} required={required} />

      {/* Input fields and pin button */}
      <XStack space="$2">
        {/* Latitude input */}
        <YStack flex={1}>
          <Input
            value={latitude || ''}
            max={90} // Latitude can't be higher than 90
            min={-90} // Latitude can't be less than -90
            onChangeText={(value) =>
              onLatitudeChange(formatCoordinateInput(value))
            }
            placeholder={latitudePlaceholder}
            keyboardType="numeric"
            flex={1}
          />
          <RequiredIndicator show={required} />
        </YStack>

        {/* Longitude input */}
        <YStack flex={1}>
          <Input
            value={longitude || ''}
            max={180} // Longitude can't be higher than 180
            min={-180} // Longitude can't be less than -180
            onChangeText={(value) =>
              onLongitudeChange(formatCoordinateInput(value))
            }
            placeholder={longitudePlaceholder}
            keyboardType="numeric"
            flex={1}
          />
          <RequiredIndicator show={required} />
        </YStack>

        {/* Pin button */}
        {onPinPress && (
          <Button
            onPress={() => setPinVisible(true)}
            icon={<Ionicons size={15} name="location" color="white" />}
          />
        )}
      </XStack>
      <PinModal
        visible={pinVisible}
        onPin={(lat, lng) => {
          onLatitudeChange(formatCoordinateInput(`${lat}`));
          onLongitudeChange(formatCoordinateInput(`${lng}`));
        }}
        onClose={() => setPinVisible(false)}
      />
      {country && !country.allowed && (
        <FieldError message={t('reportCreation.errors.countryForbidden')} />
      )}
      {error && error.statusCode === 400 && (
        <FieldError message={t('reportCreation.errors.noCountryCoordinates')} />
      )}
    </YStack>
  );
};
